// src/modules/moduleDashboards/pages/DashboardBuilderPage.jsx
// Visual, drag-and-drop authoring UI for Module Dashboards (admin).
//
// Dashboards are 1:1 with ROLE permission sets: the dropdown lists every
// PermissionSet with scope='role', and the layout you compose belongs to the role
// you picked. Saving also binds it (writes dashboardKey + dashboardMaxScope onto
// that set), so every user in the role lands on it — there is no separate
// "attach roles" step.
//
//   • Pick the role whose dashboard you're editing.
//   • Search the palette, drag widgets onto the canvas.
//   • Reorder widgets on the canvas by dragging; duplicate or remove in place.
//   • Select a widget to edit its title, width, data scope (Branch/Region/Global)
//     and widget-specific config in the right-hand drawer.
//   • Set the role's max data scope, then Save.
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  Row, Col, Card, Button, Select, Space, Typography, Drawer, Input, Segmented,
  Switch, Divider, Tag, Empty, message, Spin, Tooltip, Popconfirm, DatePicker,
  Badge, Collapse, Alert,
} from 'antd';
import dayjs from 'dayjs';
import {
  SaveOutlined, DeleteOutlined, DragOutlined, PlusOutlined, SettingOutlined,
  AppstoreOutlined, LockOutlined, SearchOutlined, CopyOutlined, ClearOutlined,
  LineChartOutlined, UnorderedListOutlined, ThunderboltOutlined, NumberOutlined,
  FieldTimeOutlined, ColumnWidthOutlined, GlobalOutlined, EditOutlined,
  TeamOutlined, CheckCircleOutlined, BlockOutlined, TableOutlined, LinkOutlined,
  GoldOutlined, CheckSquareOutlined, SortAscendingOutlined,
} from '@ant-design/icons';
import { fetchWidgetCatalog, fetchLayout, saveLayout } from '../api';

const { Title, Text, Paragraph } = Typography;

const uid = () => `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

const SPAN_OPTIONS = [
  { label: '¼', value: 6 },
  { label: '⅓', value: 8 },
  { label: '½', value: 12 },
  { label: '⅔', value: 16 },
  { label: 'Full', value: 24 },
];

// Visual identity per widget kind — drives accents, chips and previews.
const KIND_META = {
  kpi: { color: '#1677ff', bg: '#e6f4ff', icon: <NumberOutlined />, label: 'KPI' },
  list: { color: '#389e0d', bg: '#f6ffed', icon: <UnorderedListOutlined />, label: 'List' },
  chart: { color: '#722ed1', bg: '#f9f0ff', icon: <LineChartOutlined />, label: 'Chart' },
  metrics: { color: '#08979c', bg: '#e6fffb', icon: <BlockOutlined />, label: 'Metrics' },
  table: { color: '#c41d7f', bg: '#fff0f6', icon: <TableOutlined />, label: 'Table' },
  action: { color: '#cf1322', bg: '#fff1f0', icon: <CheckSquareOutlined />, label: 'Actionable' },
  shortcut: { color: '#d46b08', bg: '#fff7e6', icon: <ThunderboltOutlined />, label: 'Shortcut' },
};
const kindMeta = (kind) => KIND_META[kind] || KIND_META.kpi;

const CATEGORY_EMOJI = {
  Leads: '🎯', Approvals: '✅', 'My Approvals': '🙋', Billing: '🧾', Pledge: '💍',
  Treasury: '🏦', Accounting: '📒', 'Walk-ins': '🚶', Inventory: '📦', HR: '🧑‍💼',
  Customers: '🤝', IBCT: '🔁', 'Gold Vault': '🪙', Shortcuts: '⚡',
  'Accounting Overview': '📊', 'Profit & Loss': '📈', 'Balance Sheet': '⚖️',
  'Stock Register': '🪙', 'GST Centre': '🧮', 'Fund Management': '💰',
  'Day Closure': '🌙',
};

// Palette groups render in this order; everything unlisted follows alphabetically.
// Company Accounting sits together, right after the operational modules.
const CATEGORY_ORDER = [
  'Leads', 'Approvals', 'My Approvals', 'Billing', 'Pledge', 'Walk-ins',
  'Treasury', 'Day Closure', 'IBCT', 'Inventory', 'Gold Vault', 'Customers', 'HR',
  'Accounting', 'Accounting Overview', 'Profit & Loss', 'Balance Sheet',
  'Stock Register', 'GST Centre', 'Fund Management', 'Shortcuts',
];

const TIME_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_90_days', label: 'Last 90 days' },
  { value: 'this_year', label: 'This year' },
  { value: 'all_time', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
];

// Same fixed-order categorical palette the rendered dashboard uses, so a preview
// looks like the thing it previews.
const PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#4a3aa7'];

// Deterministic pseudo-random series per widget key so previews are stable.
const seededVals = (key, n, min = 18, max = 92) => {
  let h = 0;
  for (const c of String(key)) h = (h * 31 + c.charCodeAt(0)) % 9973;
  return Array.from({ length: n }, (_, i) => {
    h = (h * 137 + (i + 1) * 61) % 9973;
    return min + (h % (max - min));
  });
};

// ── Mock preview rendered inside each canvas card ────────
function MiniPreview({ entry, widgetKey }) {
  const kind = entry?.kind || 'kpi';
  const meta = kindMeta(kind);
  const vals = seededVals(widgetKey || entry?.key || 'w', 8);

  if (kind === 'kpi') {
    const pts = vals.map((v, i) => `${(i / (vals.length - 1)) * 100},${36 - (v / 100) * 30}`).join(' ');
    return (
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: meta.color, lineHeight: 1.1 }}>
            {(vals[0] * 137).toLocaleString()}
          </div>
          <Text type="secondary" style={{ fontSize: 11 }}>sample value</Text>
        </div>
        <svg viewBox="0 0 100 36" style={{ width: 90, height: 32, flexShrink: 0 }} preserveAspectRatio="none">
          <polyline points={pts} fill="none" stroke={meta.color} strokeWidth="2.5" strokeLinecap="round" opacity="0.85" />
        </svg>
      </div>
    );
  }

  if (kind === 'chart') {
    const type = entry?.chartType || 'line';
    if (type === 'pie' || type === 'donut') {
      const segs = vals.slice(0, 4);
      const total = segs.reduce((a, b) => a + b, 0);
      let acc = 0;
      const stops = segs.map((v, i) => {
        const from = (acc / total) * 360; acc += v;
        const to = (acc / total) * 360;
        return `${PALETTE[i % PALETTE.length]} ${from}deg ${to}deg`;
      }).join(', ');
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', flexShrink: 0,
            background: `conic-gradient(${stops})`,
            ...(type === 'donut' ? { WebkitMask: 'radial-gradient(circle, transparent 34%, #000 36%)', mask: 'radial-gradient(circle, transparent 34%, #000 36%)' } : {}),
          }} />
          <div style={{ flex: 1 }}>
            {segs.slice(0, 3).map((v, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{ width: 8, height: 8, borderRadius: 2, background: PALETTE[i % PALETTE.length] }} />
                <div style={{ height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.08)', width: `${40 + v / 2}%` }} />
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (type === 'column' || type === 'bar') {
      return (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 52 }}>
          {vals.map((v, i) => (
            <div key={i} style={{
              flex: 1, borderRadius: '4px 4px 2px 2px',
              height: `${Math.max(v, 14)}%`,
              background: `linear-gradient(180deg, ${meta.color} 0%, ${meta.color}55 100%)`,
            }} />
          ))}
        </div>
      );
    }
    // line / area
    const pts = vals.map((v, i) => [(i / (vals.length - 1)) * 100, 48 - (v / 100) * 42]);
    const line = pts.map((p) => p.join(',')).join(' ');
    return (
      <svg viewBox="0 0 100 52" style={{ width: '100%', height: 52 }} preserveAspectRatio="none">
        {type === 'area' && (
          <polygon points={`0,52 ${line} 100,52`} fill={meta.color} opacity="0.15" />
        )}
        <polyline points={line} fill="none" stroke={meta.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="1.8" fill={meta.color} />)}
      </svg>
    );
  }

  if (kind === 'action') {
    return (
      <div>
        {[0, 1].map((i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ height: 5, width: `${50 + (vals[i] % 25)}%`, borderRadius: 2, background: 'rgba(0,0,0,0.12)', marginBottom: 3 }} />
              <div style={{ height: 4, width: `${30 + (vals[i + 1] % 20)}%`, borderRadius: 2, background: 'rgba(0,0,0,0.06)' }} />
            </div>
            <span style={{ height: 14, width: 30, borderRadius: 3, background: `${meta.color}22`, border: `1px solid ${meta.color}66` }} />
            <span style={{ height: 14, width: 26, borderRadius: 3, background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.12)' }} />
          </div>
        ))}
        <div style={{
          height: 18, borderRadius: 4, marginTop: 8, border: `1px solid ${meta.color}55`, background: meta.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600, color: meta.color,
        }}>
          Open My Approvals →
        </div>
      </div>
    );
  }

  if (kind === 'metrics') {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: 'rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, overflow: 'hidden' }}>
        {vals.slice(0, 6).map((v, i) => (
          <div key={i} style={{ background: '#fff', padding: '5px 6px' }}>
            <div style={{ height: 4, width: `${45 + (v % 40)}%`, borderRadius: 2, background: 'rgba(0,0,0,0.10)', marginBottom: 4 }} />
            <div style={{ fontSize: 11, fontWeight: 700, color: i % 3 === 0 ? meta.color : 'rgba(0,0,0,0.75)' }}>
              {(v * 137).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (kind === 'table') {
    return (
      <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 6, padding: '4px 7px', background: meta.bg }}>
          {[38, 26, 20].map((w, i) => (
            <div key={i} style={{ height: 5, width: `${w}%`, borderRadius: 2, background: `${meta.color}55` }} />
          ))}
        </div>
        {vals.slice(0, 3).map((v, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, padding: '4px 7px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
            <div style={{ height: 5, width: `${30 + (v % 12)}%`, borderRadius: 2, background: 'rgba(0,0,0,0.10)' }} />
            <div style={{ height: 5, width: '24%', borderRadius: 2, background: 'rgba(0,0,0,0.07)' }} />
            <div style={{ height: 5, width: '18%', borderRadius: 2, background: 'rgba(0,0,0,0.07)', marginLeft: 'auto' }} />
          </div>
        ))}
      </div>
    );
  }

  if (kind === 'list') {
    return (
      <div>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < 2 ? 7 : 0 }}>
            <span style={{
              width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
              background: `${PALETTE[(vals[i] + i) % PALETTE.length]}33`,
              border: `1.5px solid ${PALETTE[(vals[i] + i) % PALETTE.length]}`,
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: 6, borderRadius: 3, background: 'rgba(0,0,0,0.10)', width: `${45 + vals[i] / 3}%`, marginBottom: 3 }} />
              <div style={{ height: 5, borderRadius: 3, background: 'rgba(0,0,0,0.05)', width: `${25 + vals[i + 1] / 4}%` }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // shortcut
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px',
      borderRadius: 20, background: meta.bg, color: meta.color, fontSize: 12, fontWeight: 600,
      border: `1px solid ${meta.color}44`,
    }}>
      <ThunderboltOutlined /> quick action →
    </div>
  );
}

// ── Palette item (drag source) ───────────────────────────
function PaletteItem({ entry }) {
  const meta = kindMeta(entry.kind);
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'PALETTE_WIDGET',
    item: { entry },
    collect: (m) => ({ isDragging: m.isDragging() }),
  }), [entry]);

  return (
    <div
      ref={drag}
      className="dbw-palette-item"
      style={{
        borderRadius: 10, padding: '8px 10px', marginBottom: 8,
        cursor: 'grab', opacity: isDragging ? 0.4 : 1,
        background: '#fff', border: '1px solid #ececec',
        borderLeft: `3px solid ${meta.color}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          width: 24, height: 24, borderRadius: 6, display: 'inline-flex', flexShrink: 0,
          alignItems: 'center', justifyContent: 'center', background: meta.bg, color: meta.color, fontSize: 12,
        }}>
          {meta.icon}
        </span>
        <Text style={{ fontSize: 13, flex: 1 }} ellipsis={{ tooltip: entry.label }}>{entry.label}</Text>
        {entry.timeBased && (
          <Tooltip title="Time-range aware — pick a fetch window per widget">
            <FieldTimeOutlined style={{ color: '#13c2c2', fontSize: 12 }} />
          </Tooltip>
        )}
        {entry.privileged && (
          <Tooltip title="Executive metric — only shown to Admin / Area / Global users">
            <LockOutlined style={{ color: '#fa8c16', fontSize: 12 }} />
          </Tooltip>
        )}
        <DragOutlined style={{ color: '#c8c8c8', fontSize: 12 }} />
      </div>
      <div style={{ marginTop: 3, paddingLeft: 32 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>{entry.description}</Text>
      </div>
    </div>
  );
}

// ── Canvas widget (drag to reorder + click to select) ────
function CanvasWidget({ widget, index, entry, selected, onSelect, onRemove, onDuplicate, moveWidget }) {
  const ref = useRef(null);
  const meta = kindMeta(entry?.kind);
  const [, drop] = useDrop({
    accept: 'CANVAS_WIDGET',
    hover(item) {
      if (item.index === index) return;
      moveWidget(item.index, index);
      item.index = index;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: 'CANVAS_WIDGET',
    item: { index },
    collect: (m) => ({ isDragging: m.isDragging() }),
  });
  drag(drop(ref));

  return (
    <div ref={ref} style={{ opacity: isDragging ? 0.35 : 1 }}>
      <Card
        size="small"
        className="dbw-canvas-card"
        onClick={() => onSelect(widget.id)}
        style={{
          borderRadius: 12, overflow: 'hidden', cursor: 'pointer',
          borderColor: selected ? meta.color : '#eef0f3',
          boxShadow: selected ? `0 0 0 2px ${meta.color}33, 0 6px 18px rgba(0,0,0,0.08)` : '0 1px 4px rgba(0,0,0,0.04)',
        }}
        styles={{
          header: {
            minHeight: 40, padding: '0 10px',
            background: `linear-gradient(90deg, ${meta.bg} 0%, #ffffff 85%)`,
            borderBottom: '1px solid rgba(0,0,0,0.04)',
          },
          body: { padding: '10px 12px' },
        }}
        title={(
          <Space size={6}>
            <DragOutlined style={{ color: '#b9bdc4', cursor: 'grab' }} />
            <span style={{ color: meta.color, fontSize: 12 }}>{meta.icon}</span>
            <Text style={{ fontSize: 13 }} strong ellipsis>
              {widget.title || entry?.label || widget.widgetKey}
            </Text>
          </Space>
        )}
        extra={(
          <Space size={2} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Configure">
              <Button size="small" type="text" icon={<SettingOutlined />} onClick={() => onSelect(widget.id)} />
            </Tooltip>
            <Tooltip title="Duplicate">
              <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => onDuplicate(widget.id)} />
            </Tooltip>
            <Popconfirm title="Remove widget?" onConfirm={() => onRemove(widget.id)} okText="Remove" cancelText="No">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        )}
      >
        <div style={{ marginBottom: 10, minHeight: 44 }}>
          <MiniPreview entry={entry} widgetKey={widget.id} />
        </div>
        <Space wrap size={4}>
          <Tag color={meta.color} style={{ borderRadius: 10, lineHeight: '18px' }}>{entry?.kind || '?'}</Tag>
          <Tag icon={<GlobalOutlined />} style={{ borderRadius: 10, lineHeight: '18px' }}>{widget.context?.scope || 'BRANCH'}</Tag>
          <Tag icon={<ColumnWidthOutlined />} style={{ borderRadius: 10, lineHeight: '18px' }}>{Math.round(((widget.span || 12) / 24) * 100)}%</Tag>
          {entry?.timeBased && <Tag color="cyan" icon={<FieldTimeOutlined />} style={{ borderRadius: 10, lineHeight: '18px' }}>{(widget.config?.timeRange || 'this_month').replaceAll('_', ' ')}</Tag>}
          {entry?.kind !== 'shortcut' && (widget.config?.route || entry?.route) && (
            <Tooltip title="Clicking this widget opens that page">
              <Tag icon={<LinkOutlined />} style={{ borderRadius: 10, lineHeight: '18px' }}>{widget.config?.route || entry.route}</Tag>
            </Tooltip>
          )}
          {entry?.privileged && <Tag color="orange" icon={<LockOutlined />} style={{ borderRadius: 10, lineHeight: '18px' }}>exec</Tag>}
        </Space>
      </Card>
    </div>
  );
}

// ── Canvas drop zone ─────────────────────────────────────
function Canvas({ widgets, catalogByKey, selectedId, onSelect, onRemove, onDuplicate, moveWidget, onDropNew }) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'PALETTE_WIDGET',
    drop: (item) => onDropNew(item.entry),
    collect: (m) => ({ isOver: m.isOver() }),
  }), [onDropNew]);

  return (
    <div
      ref={drop}
      style={{
        minHeight: 440, borderRadius: 14, padding: 14,
        background: isOver
          ? 'radial-gradient(rgba(22,119,255,0.14) 1.2px, rgba(22,119,255,0.05) 1.2px)'
          : 'radial-gradient(rgba(0,0,0,0.07) 1.2px, transparent 1.2px)',
        backgroundSize: '18px 18px',
        border: `2px dashed ${isOver ? '#1677ff' : '#e3e5e8'}`,
        transition: 'border-color .2s, background .2s',
      }}
    >
      {widgets.length === 0 ? (
        <div style={{ textAlign: 'center', paddingTop: 90, paddingBottom: 90 }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>🎨</div>
          <Title level={5} style={{ marginBottom: 4 }}>Your canvas is empty</Title>
          <Text type="secondary">Drag widgets here from the palette to start composing this dashboard.</Text>
        </div>
      ) : (
        <Row gutter={[14, 14]}>
          {widgets.map((w, i) => (
            <Col key={w.id} xs={24} sm={12} md={Math.max(6, Math.min(24, w.span || 12))} className="dbw-fade-in">
              <CanvasWidget
                widget={w}
                index={i}
                entry={catalogByKey[w.widgetKey]}
                selected={selectedId === w.id}
                onSelect={onSelect}
                onRemove={onRemove}
                onDuplicate={onDuplicate}
                moveWidget={moveWidget}
              />
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}

// ── Config drawer for the selected widget ────────────────
function WidgetConfigDrawer({ open, widget, entry, scopes, onClose, onChange }) {
  if (!widget || !entry) return <Drawer open={open} onClose={onClose} width={380} title="Widget" />;

  const meta = kindMeta(entry.kind);
  const set = (patch) => onChange({ ...widget, ...patch });
  const setCtx = (patch) => onChange({ ...widget, context: { ...widget.context, ...patch } });
  const setCfg = (patch) => onChange({ ...widget, config: { ...widget.config, ...patch } });

  const allowedScopes = (entry.allowedScopes || ['BRANCH']).filter((s) => scopes.includes(s));

  const SectionTitle = ({ icon, children }) => (
    <Space size={6} style={{ marginBottom: 6 }}>
      <span style={{ color: meta.color }}>{icon}</span>
      <Text strong>{children}</Text>
    </Space>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={380}
      title={(
        <Space>
          <span style={{
            width: 28, height: 28, borderRadius: 8, display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', background: meta.bg, color: meta.color,
          }}>{meta.icon}</span>
          <span>{entry.label}</span>
          {entry.privileged && <Tag color="orange" icon={<LockOutlined />}>exec</Tag>}
        </Space>
      )}
      styles={{ body: { paddingTop: 12 } }}
    >
      <Paragraph type="secondary" style={{ fontSize: 12 }}>{entry.description}</Paragraph>

      <div style={{ borderRadius: 10, border: '1px solid #f0f0f0', padding: 12, marginBottom: 16, background: '#fcfcfd' }}>
        <MiniPreview entry={entry} widgetKey={widget.id} />
      </div>

      <SectionTitle icon={<EditOutlined />}>Title</SectionTitle>
      <Input
        style={{ margin: '0 0 16px' }}
        value={widget.title}
        placeholder={entry.label}
        onChange={(e) => set({ title: e.target.value })}
      />

      <SectionTitle icon={<ColumnWidthOutlined />}>Width</SectionTitle>
      <div style={{ margin: '0 0 16px' }}>
        <Segmented
          options={SPAN_OPTIONS}
          value={widget.span}
          onChange={(v) => set({ span: v })}
        />
      </div>

      <SectionTitle icon={<GlobalOutlined />}>Data context (scope)</SectionTitle>
      <div style={{ margin: '0 0 6px' }}>
        <Select
          style={{ width: '100%' }}
          value={widget.context?.scope}
          options={allowedScopes.map((s) => ({ label: s, value: s }))}
          onChange={(v) => setCtx({ scope: v })}
        />
      </div>
      <Text type="secondary" style={{ fontSize: 11 }}>
        Branch-scoped viewers always see only their own branch; this is the widest the widget may reach for privileged viewers.
      </Text>

      {/* Fetch type (time range) — only for time-based widgets */}
      {entry.timeBased && (
        <>
          <Divider />
          <SectionTitle icon={<FieldTimeOutlined />}>Fetch type (time range)</SectionTitle>
          <Select
            style={{ width: '100%', margin: '0 0 6px' }}
            value={widget.config?.timeRange || 'this_month'}
            options={TIME_RANGE_OPTIONS}
            onChange={(v) => setCfg({ timeRange: v })}
          />
          {widget.config?.timeRange === 'custom' && (
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              value={[
                widget.config?.customStart ? dayjs(widget.config.customStart) : null,
                widget.config?.customEnd ? dayjs(widget.config.customEnd) : null,
              ]}
              onChange={(_, strs) => setCfg({ customStart: strs?.[0] || null, customEnd: strs?.[1] || null })}
            />
          )}
          <div><Text type="secondary" style={{ fontSize: 11 }}>Controls the date window this widget aggregates over.</Text></div>
        </>
      )}

      {/* Kind-specific config */}
      {entry.defaultConfig?.limit !== undefined && (
        <>
          <Divider />
          <SectionTitle icon={<UnorderedListOutlined />}>Rows / items</SectionTitle>
          <Input
            type="number" min={1} max={40} style={{ margin: '0 0 6px' }}
            value={widget.config?.limit ?? entry.defaultConfig.limit}
            onChange={(e) => setCfg({ limit: Number(e.target.value) })}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            How many rows, slices or bars this widget shows before folding the rest away.
          </Text>
        </>
      )}

      {/* Which end of the queue an actionable list shows first. */}
      {entry.defaultConfig?.order !== undefined && (
        <>
          <Divider />
          <SectionTitle icon={<SortAscendingOutlined />}>Show first</SectionTitle>
          <Segmented
            options={[{ label: 'Latest requests', value: 'newest' }, { label: 'Longest waiting', value: 'oldest' }]}
            value={widget.config?.order ?? entry.defaultConfig.order}
            onChange={(v) => setCfg({ order: v })}
          />
          <div style={{ marginTop: 6 }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              “Longest waiting” puts the most overdue requests at the top.
            </Text>
          </div>
        </>
      )}

      {entry.kind === 'action' && (
        <Alert
          type="info" showIcon style={{ marginTop: 16 }}
          message="This widget takes action"
          description="Viewers can approve or reject straight from the card (a comment is required, as on the inbox). The approval engine still re-checks that they are a current-step approver, and the Approve/Reject buttons follow their Workflow permissions."
        />
      )}

      {/* Month-bucketed widgets: how far back to look when no range is pinned. */}
      {entry.defaultConfig?.months !== undefined && (
        <>
          <Divider />
          <SectionTitle icon={<FieldTimeOutlined />}>Months to plot</SectionTitle>
          <Select
            style={{ width: '100%', margin: '0 0 6px' }}
            value={widget.config?.months ?? entry.defaultConfig.months}
            options={[3, 6, 12, 18, 24].map((m) => ({ label: `Last ${m} months`, value: m }))}
            onChange={(v) => setCfg({ months: v })}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Used when the fetch window is All time; an explicit range always wins.
          </Text>
        </>
      )}

      {/* Stock Register widgets can be pinned to one metal. */}
      {entry.defaultConfig?.metalType !== undefined && (
        <>
          <Divider />
          <SectionTitle icon={<GoldOutlined />}>Metal</SectionTitle>
          <Segmented
            options={[{ label: 'Gold', value: 'GOLD' }, { label: 'Silver', value: 'SILVER' }, { label: 'Both', value: 'ALL' }]}
            value={widget.config?.metalType ?? entry.defaultConfig.metalType}
            onChange={(v) => setCfg({ metalType: v })}
          />
        </>
      )}

      {/* Count-vs-value switch for widgets that can measure either. */}
      {entry.defaultConfig?.metric !== undefined && (
        <>
          <Divider />
          <SectionTitle icon={<NumberOutlined />}>Measure</SectionTitle>
          <Segmented
            options={[{ label: 'Posted value (₹)', value: 'value' }, { label: 'Entry count', value: 'count' }]}
            value={widget.config?.metric ?? entry.defaultConfig.metric}
            onChange={(v) => setCfg({ metric: v })}
          />
        </>
      )}

      {/* Deep link — where the widget's "open" arrow takes the viewer. */}
      {entry.kind !== 'shortcut' && entry.route && (
        <>
          <Divider />
          <SectionTitle icon={<LinkOutlined />}>Opens</SectionTitle>
          <Input
            style={{ margin: '0 0 6px' }}
            placeholder={entry.route}
            value={widget.config?.route ?? ''}
            onChange={(e) => setCfg({ route: e.target.value || undefined })}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            Clicking the widget opens <Text code style={{ fontSize: 11 }}>{widget.config?.route || entry.route}</Text>. Leave blank for the default.
          </Text>
        </>
      )}

      {'mine' in (entry.defaultConfig || {}) && (
        <>
          <Divider />
          <Space>
            <Switch checked={!!widget.config?.mine} onChange={(v) => setCfg({ mine: v })} />
            <Text>Only mine (assigned to / awaiting me)</Text>
          </Space>
        </>
      )}

      {entry.kind === 'shortcut' && (
        <>
          <Divider />
          <SectionTitle icon={<ThunderboltOutlined />}>Button label</SectionTitle>
          <Input style={{ margin: '0 0 12px' }}
            value={widget.config?.label} onChange={(e) => setCfg({ label: e.target.value })} />
          <Text strong>Route</Text>
          <Input style={{ margin: '6px 0 12px' }} placeholder="/leads"
            value={widget.config?.route} onChange={(e) => setCfg({ route: e.target.value })} />
          <Text strong>Icon (Ant Design icon name)</Text>
          <Input style={{ margin: '6px 0' }} placeholder="ArrowRightOutlined"
            value={widget.config?.icon} onChange={(e) => setCfg({ icon: e.target.value })} />
        </>
      )}
    </Drawer>
  );
}

// ── Role binding panel ───────────────────────────────────
// This dashboard belongs to exactly one role permission set — the one picked in
// the header dropdown. All this panel does is show that link and let the admin set
// how wide the role's data may reach; both are persisted by the main Save, which
// writes dashboardKey + dashboardMaxScope onto the role's PermissionSet (the
// fields the permission resolver reads).
function RoleBindingPanel({ role, maxScope, onMaxScopeChange, dashboardLabelByKey }) {
  if (!role) return null;

  return (
    <Card
      size="small"
      style={{ marginBottom: 12, borderRadius: 12 }}
      title={(
        <Space wrap>
          <TeamOutlined /> Who sees this dashboard
          <Tag color="blue" style={{ marginInlineEnd: 0 }}>{role.label}</Tag>
          {role.roleKey && <Tag style={{ marginInlineEnd: 0 }}>{role.roleKey}</Tag>}
          {role.bound
            ? <Tag color="green" icon={<CheckCircleOutlined />} style={{ marginInlineEnd: 0 }}>attached</Tag>
            : <Tag color="gold" style={{ marginInlineEnd: 0 }}>attaches on save</Tag>}
        </Space>
      )}
      extra={(
        <Space size={6}>
          <Text type="secondary" style={{ fontSize: 12 }}>Max data scope</Text>
          <Select
            size="small" style={{ width: 120 }} value={maxScope || 'BRANCH'}
            onChange={onMaxScopeChange}
            options={[
              { label: 'Branch', value: 'BRANCH' },
              { label: 'Area', value: 'AREA' },
              { label: 'Global', value: 'GLOBAL' },
            ]}
          />
        </Space>
      )}
    >
      <Text type="secondary" style={{ fontSize: 12 }}>
        Every user in the <Text strong>{role.label}</Text> role permission set lands on this dashboard.
        Max data scope is the widest a widget here may reach — Branch-scoped viewers still only ever see their own branch.
      </Text>
      {role.boundElsewhere && (
        <Alert
          type="warning" showIcon style={{ marginTop: 10 }}
          message={`This role currently points at “${dashboardLabelByKey[role.boundElsewhere] || role.boundElsewhere}”. Saving moves it back to its own dashboard.`}
        />
      )}
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────
function BuilderInner() {
  const [catalog, setCatalog] = useState({ widgets: [], dashboards: [], scopes: [] });
  const [dashboardKey, setDashboardKey] = useState(null);
  const [role, setRole] = useState(null);       // the role permission set this dashboard belongs to
  const [maxScope, setMaxScope] = useState('BRANCH');
  const [name, setName] = useState('');
  const [isPublished, setIsPublished] = useState(true);
  const [widgets, setWidgets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');
  const [messageApi, contextHolder] = message.useMessage();

  const catalogByKey = useMemo(
    () => catalog.widgets.reduce((acc, w) => { acc[w.key] = w; return acc; }, {}),
    [catalog.widgets]
  );
  const dashboardLabelByKey = useMemo(
    () => catalog.dashboards.reduce((acc, d) => { acc[d.key] = d.label; return acc; }, {}),
    [catalog.dashboards]
  );
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const g = {};
    catalog.widgets
      .filter((w) => !q || `${w.label} ${w.description} ${w.category} ${w.kind}`.toLowerCase().includes(q))
      .forEach((w) => { (g[w.category] = g[w.category] || []).push(w); });
    return g;
  }, [catalog.widgets, search]);

  // Known categories first, in the curated order; anything new sorts in after.
  const categories = useMemo(() => {
    const keys = Object.keys(grouped);
    const rank = (c) => { const i = CATEGORY_ORDER.indexOf(c); return i === -1 ? CATEGORY_ORDER.length : i; };
    return keys.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }, [grouped]);

  const stats = useMemo(() => {
    const s = { total: widgets.length, kpi: 0, chart: 0, list: 0, metrics: 0, table: 0, shortcut: 0, exec: 0 };
    widgets.forEach((w) => {
      const e = catalogByKey[w.widgetKey];
      if (!e) return;
      s[e.kind] = (s[e.kind] || 0) + 1;
      if (e.privileged) s.exec += 1;
    });
    return s;
  }, [widgets, catalogByKey]);

  // Load catalog once.
  useEffect(() => {
    (async () => {
      try {
        const cat = await fetchWidgetCatalog();
        setCatalog(cat);
        if (cat.dashboards?.length) setDashboardKey(cat.dashboards[0].key);
      } catch (e) {
        messageApi.error('Failed to load widget catalog');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the selected role's dashboard layout (+ its current binding).
  useEffect(() => {
    if (!dashboardKey) return;
    (async () => {
      try {
        setLoading(true);
        const layout = await fetchLayout(dashboardKey);
        setName(layout.name || '');
        setIsPublished(layout.isPublished !== false);
        setWidgets(Array.isArray(layout.widgets) ? layout.widgets : []);
        setRole(layout.role || null);
        setMaxScope(layout.role?.maxScope || 'BRANCH');
        setSelectedId(null);
        setDirty(false);
      } catch (e) {
        messageApi.error('Failed to load layout');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardKey]);

  const touch = () => setDirty(true);

  const addWidget = useCallback((entry) => {
    setWidgets((prev) => [...prev, {
      id: uid(),
      widgetKey: entry.key,
      title: '',
      span: entry.defaultSpan || 12,
      context: { scope: entry.allowedScopes?.[0] || 'BRANCH', branch: null, region: null },
      config: { ...(entry.defaultConfig || {}) },
    }]);
    setDirty(true);
  }, []);

  const moveWidget = useCallback((from, to) => {
    setWidgets((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
    setDirty(true);
  }, []);

  const removeWidget = useCallback((id) => {
    setWidgets((prev) => prev.filter((w) => w.id !== id));
    setSelectedId((cur) => (cur === id ? null : cur));
    setDirty(true);
  }, []);

  const duplicateWidget = useCallback((id) => {
    setWidgets((prev) => {
      const i = prev.findIndex((w) => w.id === id);
      if (i === -1) return prev;
      const copy = JSON.parse(JSON.stringify(prev[i]));
      copy.id = uid();
      const next = [...prev];
      next.splice(i + 1, 0, copy);
      return next;
    });
    setDirty(true);
  }, []);

  const clearAll = useCallback(() => {
    setWidgets([]);
    setSelectedId(null);
    setDirty(true);
  }, []);

  const updateWidget = useCallback((updated) => {
    setWidgets((prev) => prev.map((w) => (w.id === updated.id ? updated : w)));
    setDirty(true);
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      // Saving persists the layout AND binds it to the role (dashboardKey + maxScope).
      const res = await saveLayout(dashboardKey, { name, isPublished, widgets, maxScope });
      if (res?.role) {
        setRole(res.role);
        setMaxScope(res.role.maxScope || 'BRANCH');
        // Keep the dropdown's "has layout"/binding badges in step with what we just saved.
        setCatalog((prev) => ({
          ...prev,
          dashboards: prev.dashboards.map((d) => (
            d.key === dashboardKey ? { ...d, ...res.role, hasLayout: widgets.length > 0 } : d
          )),
        }));
      }
      setDirty(false);
      messageApi.success(`Dashboard saved for ${res?.role?.label || 'this role'}`);
    } catch (e) {
      messageApi.error(e.response?.data?.message || 'Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  const selected = widgets.find((w) => w.id === selectedId) || null;

  const statChip = (label, value, color) => (
    <span key={label} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 10px',
      borderRadius: 20, background: 'rgba(255,255,255,0.16)', color: '#fff', fontSize: 12,
      border: '1px solid rgba(255,255,255,0.25)',
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      {value} {label}
    </span>
  );

  return (
    <div style={{ padding: 12 }}>
      {contextHolder}
      <style>{`
        .dbw-palette-item { transition: transform .15s ease, box-shadow .15s ease, border-color .15s ease; }
        .dbw-palette-item:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(0,0,0,0.08); border-color: #d0d5dd; }
        .dbw-canvas-card { transition: transform .18s ease, box-shadow .18s ease; }
        .dbw-canvas-card:hover { transform: translateY(-2px); box-shadow: 0 10px 24px rgba(0,0,0,0.10) !important; }
        @keyframes dbwFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .dbw-fade-in { animation: dbwFadeIn .25s ease both; }
      `}</style>

      {/* Hero header */}
      <div style={{
        borderRadius: 16, padding: '18px 22px', marginBottom: 14, color: '#fff',
        background: 'linear-gradient(120deg, #1d39c4 0%, #531dab 55%, #9e1068 100%)',
        boxShadow: '0 8px 24px rgba(29,57,196,0.25)',
      }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space align="center" size={12}>
              <span style={{
                width: 44, height: 44, borderRadius: 12, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', background: 'rgba(255,255,255,0.18)', fontSize: 22,
              }}>
                <AppstoreOutlined />
              </span>
              <div>
                <Title level={3} style={{ margin: 0, color: '#fff' }}>Dashboard Builder</Title>
                <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
                  Design the dashboard for a role — everyone in that role permission set lands on it.
                </Text>
              </div>
            </Space>
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {statChip('widgets', stats.total, '#fff')}
              {statChip('KPIs', stats.kpi || 0, KIND_META.kpi.color)}
              {statChip('charts', stats.chart || 0, '#b37feb')}
              {statChip('panels', stats.metrics || 0, '#5cdbd3')}
              {statChip('tables', stats.table || 0, '#ff85c0')}
              {statChip('lists', stats.list || 0, '#95de64')}
              {statChip('exec', stats.exec, '#ffc069')}
            </div>
          </Col>
          <Col>
            <Space wrap>
              {dirty && <Tag color="gold" style={{ marginInlineEnd: 0 }}>● Unsaved changes</Tag>}
              <Select
                style={{ width: 300 }}
                value={dashboardKey}
                onChange={setDashboardKey}
                showSearch
                optionFilterProp="searchText"
                placeholder="Select a role"
                notFoundContent="No role permission sets found"
                options={catalog.dashboards.map((d) => ({
                  value: d.key,
                  searchText: `${d.label} ${d.roleKey || ''}`,
                  label: (
                    <Space size={6} style={{ width: '100%' }}>
                      <TeamOutlined style={{ color: '#8c8c8c' }} />
                      <span style={{ flex: 1 }}>{d.label}</span>
                      {d.hasLayout
                        ? <Tag color="green" style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}>built</Tag>
                        : <Tag style={{ marginInlineEnd: 0, fontSize: 10, lineHeight: '16px' }}>empty</Tag>}
                    </Space>
                  ),
                }))}
              />
              <Badge dot={dirty} offset={[-4, 4]}>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}
                  disabled={!dashboardKey}
                  style={{ background: '#fff', color: '#1d39c4', fontWeight: 600, border: 'none' }}>
                  Save
                </Button>
              </Badge>
            </Space>
          </Col>
        </Row>
      </div>

      {!loading && catalog.dashboards.length === 0 && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 12 }}
          message="No role permission sets found"
          description="Dashboards are built per role. Create a permission set with scope “Role” first — it will then appear in the dropdown above."
        />
      )}

      <Spin spinning={loading}>
        <Row gutter={16}>
          {/* Palette */}
          <Col xs={24} md={7} lg={6}>
            <Card
              size="small"
              title={<Space><PlusOutlined /> Widgets <Tag style={{ marginInlineEnd: 0 }}>{catalog.widgets.length}</Tag></Space>}
              style={{ position: 'sticky', top: 12, borderRadius: 12 }}
              styles={{ body: { maxHeight: '72vh', overflowY: 'auto', paddingTop: 10 } }}
            >
              <Input
                allowClear
                prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                placeholder="Search widgets…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ marginBottom: 10, borderRadius: 8 }}
              />
              {categories.length === 0 && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No widgets match" />
              )}
              <Collapse
                ghost
                size="small"
                defaultActiveKey={categories}
                key={search ? 'searching' : 'idle'} // re-open all groups while searching
                items={categories.map((cat) => ({
                  key: cat,
                  label: (
                    <Space size={6}>
                      <span>{CATEGORY_EMOJI[cat] || '🧩'}</span>
                      <Text strong style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4 }}>{cat}</Text>
                      <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>{grouped[cat].length}</Tag>
                    </Space>
                  ),
                  children: grouped[cat].map((entry) => <PaletteItem key={entry.key} entry={entry} />),
                }))}
              />
            </Card>
          </Col>

          {/* Canvas */}
          <Col xs={24} md={17} lg={18}>
            <Card size="small" style={{ marginBottom: 12, borderRadius: 12 }}>
              <Row justify="space-between" align="middle" gutter={[8, 8]}>
                <Col>
                  <Space wrap>
                    <Text strong>Layout name</Text>
                    <Input style={{ width: 240 }} value={name} placeholder="e.g. Operations home"
                      onChange={(e) => { setName(e.target.value); touch(); }} />
                    <Divider type="vertical" />
                    <Switch checked={isPublished} onChange={(v) => { setIsPublished(v); touch(); }} />
                    <Text>{isPublished ? <Tag color="green" style={{ marginInlineEnd: 0 }}>Published</Tag> : <Tag style={{ marginInlineEnd: 0 }}>Draft (falls back to default view)</Tag>}</Text>
                  </Space>
                </Col>
                <Col>
                  <Popconfirm
                    title="Remove all widgets from this layout?"
                    onConfirm={clearAll}
                    okText="Clear all"
                    cancelText="No"
                    disabled={widgets.length === 0}
                  >
                    <Button size="small" danger type="text" icon={<ClearOutlined />} disabled={widgets.length === 0}>
                      Clear all
                    </Button>
                  </Popconfirm>
                </Col>
              </Row>
            </Card>
            {dashboardKey && (
              <RoleBindingPanel
                role={role}
                maxScope={maxScope}
                onMaxScopeChange={(v) => { setMaxScope(v); touch(); }}
                dashboardLabelByKey={dashboardLabelByKey}
              />
            )}
            <Canvas
              widgets={widgets}
              catalogByKey={catalogByKey}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onRemove={removeWidget}
              onDuplicate={duplicateWidget}
              moveWidget={moveWidget}
              onDropNew={addWidget}
            />
          </Col>
        </Row>
      </Spin>

      <WidgetConfigDrawer
        open={!!selected}
        widget={selected}
        entry={selected ? catalogByKey[selected.widgetKey] : null}
        scopes={catalog.scopes || ['BRANCH', 'AREA', 'GLOBAL']}
        onClose={() => setSelectedId(null)}
        onChange={updateWidget}
      />
    </div>
  );
}

export default function DashboardBuilderPage() {
  return (
    <DndProvider backend={HTML5Backend}>
      <BuilderInner />
    </DndProvider>
  );
}
